/**
 * Типы собственности машины (ERP ↔ TMS).
 * Совпадает с erp-backend/src/car/ownership.py
 */
export const OWNERSHIP_OWNED = {
  value: '552ca124-fa3f-483c-b8a6-42984ee7e5ab',
  name: '1 - Собственность',
  code: '1',
};

export const OWNERSHIP_ATTRACTED = {
  value: '51739b1a-c7a6-46da-b4d0-05088fad8ead',
  name: '6 - Привлеченный',
  code: '6',
};

export const OWNERSHIP_RENTED = {
  value: 'addc685d-a523-4555-becd-4f6989847168',
  name: '3 - Аренда',
  code: '3',
};

export const CAR_OWNERSHIP_OPTIONS = [OWNERSHIP_OWNED, OWNERSHIP_ATTRACTED];
export const TRAILER_OWNERSHIP_OPTIONS = [OWNERSHIP_OWNED, OWNERSHIP_RENTED];

export function ownershipCodeFromName(name) {
  if (!name || !String(name).trim()) return null;
  const match = String(name).match(/^\s*(\d+)\b/);
  return match ? match[1] : null;
}

export function findOwnershipOption(valueOrCode) {
  if (!valueOrCode) return null;
  const key = String(valueOrCode);
  return (
    CAR_OWNERSHIP_OPTIONS.find(
      (opt) => opt.value === key || opt.code === key || opt.name === key,
    ) || null
  );
}

export function findOwnershipOptionIn(options, valueOrCode) {
  if (!valueOrCode) return null;
  const key = String(valueOrCode);
  return options.find((opt) => opt.value === key || opt.code === key || opt.name === key) || null;
}

export function digitsOnly(value) {
  if (value == null || value === '') return null;
  const clear = String(value).replace(/\D/g, '');
  return clear || null;
}

/**
 * Достаёт из компании значения для полей «конечный перевозчик» (тип 6).
 * Машина в ERP не хранит company_id — компания приходит из контекста исполнителя.
 */
export function extractCompanyCarrierDefaults(company) {
  if (!company || typeof company !== 'object') {
    return { final_carrier_name: '', final_carrier_inn: '' };
  }
  const name = company.name ?? company.company_name ?? '';
  return {
    final_carrier_name: String(name || '').trim(),
    final_carrier_inn: digitsOnly(company.inn) || '',
  };
}

/**
 * Автозаполнение final_* из компании при выборе типа 6.
 * Перезаписывает только пустые поля или те, что ещё помечены как autofill
 * (пользовательское редактирование не затирается).
 *
 * @param {{
 *   company: object|null,
 *   currentName?: string,
 *   currentInn?: string,
 *   filledFromCompany?: { name?: boolean, inn?: boolean },
 * }} args
 */
export function resolveAttractedCarrierAutofill({
  company,
  currentName = '',
  currentInn = '',
  filledFromCompany = {},
} = {}) {
  const defaults = extractCompanyCarrierDefaults(company);
  const flags = {
    name: Boolean(filledFromCompany.name),
    inn: Boolean(filledFromCompany.inn),
  };
  const next = {
    final_carrier_name: currentName == null ? '' : String(currentName),
    final_carrier_inn: currentInn == null ? '' : String(currentInn),
    filledFromCompany: { ...flags },
  };

  const nameEmpty = !String(next.final_carrier_name).trim();
  if ((nameEmpty || flags.name) && defaults.final_carrier_name) {
    next.final_carrier_name = defaults.final_carrier_name;
    next.filledFromCompany.name = true;
  }

  const innEmpty = !digitsOnly(next.final_carrier_inn);
  if ((innEmpty || flags.inn) && defaults.final_carrier_inn) {
    next.final_carrier_inn = defaults.final_carrier_inn;
    next.filledFromCompany.inn = true;
  }

  return next;
}

/**
 * Собирает payload для POST /api/v2/cars из плоской формы UI.
 * ownershipCode: '' | '1' | '6'
 */
export function buildCarCreatePayload(form = {}, options = CAR_OWNERSHIP_OPTIONS) {
  const number = form.number != null ? String(form.number).trim() : '';
  const vin = form.vin != null ? String(form.vin).trim() : '';
  const payload = {
    number,
    vin,
  };

  if (form.glonass != null) payload.glonass = Boolean(form.glonass);
  if (form.is_trailer != null) payload.is_trailer = Boolean(form.is_trailer);

  const imei = digitsOnly(form.imei);
  if (imei) payload.imei = imei;

  const ownership = findOwnershipOptionIn(options, form.ownershipCode || form.ownership_type);
  if (ownership) {
    payload.ownership_type = {
      value: ownership.value,
      name: ownership.name,
    };

    if (ownership.code === OWNERSHIP_ATTRACTED.code) {
      const carrier =
        findOwnershipOption(form.carrierOwnershipCode || form.carrier_ownership_type) ||
        OWNERSHIP_OWNED;
      payload.carrier_ownership_type = {
        value: carrier.value,
        name: carrier.name,
      };
      const finalName =
        form.final_carrier_name != null ? String(form.final_carrier_name).trim() : '';
      const finalInn = digitsOnly(form.final_carrier_inn);
      if (finalName) payload.final_carrier_name = finalName;
      if (finalInn) payload.final_carrier_inn = finalInn;
    }
  }

  if (
    form.trailer &&
    typeof form.trailer === 'object' &&
    String(form.trailer.number || '').trim()
  ) {
    payload.trailer = { ...form.trailer, is_trailer: true };
  }

  return payload;
}

/**
 * Проверка только блока собственности (используется и при создании, и при редактировании).
 * Возвращает строку ошибки или null.
 */
export function validateCarOwnership(form = {}) {
  const options = form.is_trailer ? TRAILER_OWNERSHIP_OPTIONS : CAR_OWNERSHIP_OPTIONS;
  const ownership = findOwnershipOptionIn(options, form.ownershipCode || form.ownership_type);
  if (!ownership) return null;

  if (ownership.code === OWNERSHIP_OWNED.code) {
    if (
      (form.final_carrier_name && String(form.final_carrier_name).trim()) ||
      digitsOnly(form.final_carrier_inn) ||
      form.carrierOwnershipCode
    ) {
      return 'Для типа «1 - Собственность» поля перевозчика должны быть пустыми';
    }
    return null;
  }

  if (ownership.code === OWNERSHIP_ATTRACTED.code) {
    const carrier = findOwnershipOption(form.carrierOwnershipCode || form.carrier_ownership_type);
    if (!carrier) {
      return 'Для типа «6 - Привлеченный» укажите тип собственности перевозчика';
    }
    if (carrier.code !== OWNERSHIP_OWNED.code) {
      return 'Для типа «6 - Привлеченный» тип собственности перевозчика может быть только «1 - Собственность»';
    }
    if (!form.final_carrier_name || !String(form.final_carrier_name).trim()) {
      return 'Для типа «6 - Привлеченный» укажите наименование конечного перевозчика';
    }
    if (!digitsOnly(form.final_carrier_inn)) {
      return 'Для типа «6 - Привлеченный» укажите ИНН конечного перевозчика';
    }
  }

  return null;
}

/**
 * Клиентская проверка перед отправкой (зеркалит backend validate_car_ownership_fields).
 * Возвращает строку ошибки или null.
 */
export function validateCarCreateForm(form = {}) {
  if (!form.number || !String(form.number).trim()) {
    return 'Укажите номер автомобиля';
  }
  if (!form.vin || !String(form.vin).trim()) {
    return 'Укажите VIN';
  }
  return validateCarOwnership(form);
}

export function validateTrailerCreateForm(form = {}) {
  if (!form.number || !String(form.number).trim()) return 'Укажите номер прицепа';
  if (!form.vin || !String(form.vin).trim()) return 'Укажите VIN прицепа';
  if (!form.ownershipCode) return 'Укажите тип собственности прицепа';
  return validateCarOwnership({ ...form, is_trailer: true });
}

/**
 * Payload для PUT /api/.../cars — обновление только блока собственности.
 * Для сброса (нет типа) final_carrier_inn='' гарантирует срабатывание флага
 * update_ownership на бэкенде (ownership_type тут null).
 */
export function buildCarOwnershipUpdatePayload(form = {}) {
  const ownership = findOwnershipOption(form.ownershipCode || form.ownership_type);

  if (!ownership) {
    return {
      id: form.id,
      ownership_type: null,
      carrier_ownership_type: null,
      final_carrier_name: '',
      final_carrier_inn: '',
    };
  }

  if (ownership.code === OWNERSHIP_ATTRACTED.code) {
    return {
      id: form.id,
      ownership_type: { value: ownership.value, name: ownership.name },
      carrier_ownership_type: {
        value: OWNERSHIP_OWNED.value,
        name: OWNERSHIP_OWNED.name,
      },
      final_carrier_name:
        form.final_carrier_name != null ? String(form.final_carrier_name).trim() : '',
      final_carrier_inn: digitsOnly(form.final_carrier_inn) || '',
    };
  }

  // OWNED — связанные поля перевозчика очищаем
  return {
    id: form.id,
    ownership_type: { value: ownership.value, name: ownership.name },
    carrier_ownership_type: null,
    final_carrier_name: '',
    final_carrier_inn: '',
  };
}
